// externals
import type { Draft } from 'immer';

// libraries
import type { OfferInfoState } from '@makemydeal/dr-dash-types';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { BuildPayloadPurpose } from '@makemydeal/dr-dash-bff-types';
import { PUSH_TO_DMS_FLOW } from '../constants';
import * as deskingActionTypes from '../actionTypes/deskingActionTypes';
import {
    PRINT_CUSTOMIZABLE_PDF,
    PRINT_PDF_FOR_EMAIL,
    PRINT_PDF_FOR_TEXT,
    PUBLISH_TO_SHOWROOM
} from '../actionTypes/printPdfActionTypes';

export const storeLinksInState = (draft: Draft<OfferInfoState>, payload: any) => {
    draft.urls = {
        creditAppURL: payload.creditAppUrl,
        shopperURL: payload.shopperPageUrl,
        testDriveURL: payload.testDriveDeepLinkUrl,
        docUploadURL: payload.docUploadDeepLinkUrl
    };
};

export const shouldInitDraftSavedState = (action?: AnyFSA) => {
    const baggage = action?.payload?.baggage;
    if (!baggage?.length) {
        return false;
    }

    const baggageItem = baggage.find((item: { name: string }) => item.name === 'deal360.comparison');
    if (!baggageItem?.value?.content?.length) {
        return false;
    }

    const activeComparison = baggageItem.value.content.find(
        (item: { isPublished: boolean; status: string }) => item.status === 'active' && !item.isPublished
    );

    return !!activeComparison;
};

export const initDraftSavedSuccessState = (draft: Draft<OfferInfoState>, action?: AnyFSA) => {
    const draftSavedTime = action?.payload.offer?.createdOn;
    if (draftSavedTime) {
        draft.lastSuccessfulSaveDateString = draftSavedTime;
    }
};

export const updateOfferSavedSuccessState = (draft: Draft<OfferInfoState>, action?: AnyFSA) => {
    draft.lastSuccessfulSaveDateString = new Date().toISOString();
    if (action?.meta?.isDraft) {
        draft.isDraftBeingSaved = false;
        draft.hasDraftBeenSaved = true;
    } else {
        draft.hasBeenSaved = true;
        draft.isOfferBeingSaved = false;
    }
};

export const updateOfferSaveFailureState = (draft: Draft<OfferInfoState>, action?: AnyFSA) => {
    if (action?.meta?.isDraft) {
        draft.hasDraftBeenSaved = false;
        draft.isDraftBeingSaved = false;
    } else {
        draft.hasBeenSaved = false;
        draft.isOfferBeingSaved = false;
    }
};

export const getPurpose = (action: AnyFSA) => {
    const purposeLookup: Record<string, BuildPayloadPurpose> = {
        [PUSH_TO_DMS_FLOW]: BuildPayloadPurpose.PushToDms,
        [deskingActionTypes.CONTINUE_IN_DESKING]: BuildPayloadPurpose.ContinueInDesking,
        [PRINT_CUSTOMIZABLE_PDF]: BuildPayloadPurpose.PrintProposal,
        [PUBLISH_TO_SHOWROOM]: BuildPayloadPurpose.PublishToSalesView,
        [PRINT_PDF_FOR_TEXT]: BuildPayloadPurpose.PrintPdfForText,
        [PRINT_PDF_FOR_EMAIL]: BuildPayloadPurpose.PrintPdfForEmail
    };

    return action.payload?.purpose && Object.values(BuildPayloadPurpose).includes(action.payload.purpose)
        ? action.payload.purpose
        : purposeLookup[action.meta?.originalAction?.type] ||
              purposeLookup[action.meta?.originalAction] ||
              purposeLookup[action.meta?.source] ||
              BuildPayloadPurpose.OfferSave;
};
